import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {Text, text} from '@/components/text'
import checkIconPink from '@/images/icons/check-pink.svg'
import dashIcon from '@/images/icons/dash.svg'
import {BackgroundWrapper, Section, TitleLayout} from '@/styles/grid'
import {tokens, colors} from '@/theme'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import mq from '@/utils/media-query'

import type {FeaturesComparisonTableFragment} from '@/graphql-types'

interface Props {
    title?: string
    content: FeaturesComparisonTableFragment
}

export const FeaturesComparisonTable = ({title, content}: Props) => {
    const data = content.data

    if (!data) {
        return handleNoPrismicData()
    }

    const getTableIcon = (value: boolean) => (
        <TableIcon
            src={value ? checkIconPink : dashIcon}
            alt={value ? data.yes_value : data.no_value}
        />
    )

    return (
        <BackgroundWrapper $color="muted">
            <Section>
                {(title || data.title) && (
                    <TableTitleWrapper>
                        <Text $variant="heading-base">{title || data.title}</Text>
                    </TableTitleWrapper>
                )}

                <TableWrapper>
                    <Table>
                        <thead>
                            <tr>
                                <TableDataCell as="th" scope="column">
                                    {data.category}
                                </TableDataCell>

                                <TableDataCell as="th" scope="column">
                                    <strong>{data.pleo_heading}</strong>
                                </TableDataCell>

                                <TableDataCell as="th" scope="column">
                                    {data.corporate_cards_heading}
                                </TableDataCell>

                                <TableDataCell as="th" scope="column">
                                    {data.expense_software_heading}
                                </TableDataCell>

                                <TableDataCell as="th" scope="column">
                                    {data.corporate_cards_expense_software_heading}
                                </TableDataCell>
                            </tr>
                        </thead>

                        <tbody>
                            {data.rows.map((row, i) => (
                                <tr key={`${row.feature}-${i}`}>
                                    <TableDataCell as="th" scope="row">
                                        <FeatureText>{row.feature}</FeatureText>

                                        <FeatureSubText>{row.feature_subtext}</FeatureSubText>
                                    </TableDataCell>

                                    <TableDataCell>{getTableIcon(row.pleo)}</TableDataCell>

                                    <TableDataCell>
                                        {getTableIcon(row.corporate_cards)}
                                    </TableDataCell>

                                    <TableDataCell>
                                        {getTableIcon(row.expense_software)}
                                    </TableDataCell>

                                    <TableDataCell>
                                        {getTableIcon(row.corporate_cards_expense_software)}
                                    </TableDataCell>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </TableWrapper>
            </Section>
        </BackgroundWrapper>
    )
}

export const TableTitleWrapper = styled(TitleLayout)`
    margin-bottom: ${tokens.spacing20};

    ${mq.min('sm')} {
        margin-bottom: ${tokens.spacing40};
    }
`

export const TableWrapper = styled.div`
    padding: ${tokens.spacing20} ${tokens.spacing16};
    background: ${colors.background.default};

    ${mq.min('sm')} {
        padding: ${tokens.spacing40};
    }
`

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    tr:not(:last-child) {
        border-bottom: 1px solid ${colors.border.default};
    }

    thead th {
        padding-top: 0;
        padding-bottom: ${tokens.spacing4};
        ${text({$variant: 'paragraph-base'})}

        strong {
            ${text({$variant: 'paragraph-large-medium'})}
        }

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing20};
            ${text({$variant: 'paragraph-large'})}

            strong {
                ${text({$variant: 'paragraph-large'})}
            }
        }
    }
`

export const TableDataCell = styled.td`
    padding: ${tokens.spacing16} 0;
    text-align: left;

    &:not(:first-child) {
        width: 20%;
        margin-left: ${tokens.spacing16};
        text-align: center;
    }

    ${mq.max('sm')} {
        &:last-child {
            width: 35%;
        }
    }

    &:last-child {
        ${mq.min('sm')} {
            display: none;
        }
    }

    &:nth-child(3),
    &:nth-child(4) {
        ${mq.max('sm')} {
            display: none;
        }
    }
`

export const TableIcon = styled.img`
    margin: 0 auto;
    transform: scale(1);

    ${mq.min('sm')} {
        transform: scale(1.2);
    }
`

export const FeatureText = styled.div`
    ${text({$variant: 'paragraph-base'})}

    ${mq.min('sm')} {
        ${text({$variant: 'paragraph-large'})}
    }
`

export const FeatureSubText = styled.div`
    ${text({$variant: 'paragraph-base'})}

    ${mq.max('sm')} {
        display: none;
    }
`

export const comparisonTableFragment = graphql`
    fragment FeaturesComparisonTable on PrismicComparisonTable {
        type
        id
        data {
            title
            category
            pleo_heading
            corporate_cards_heading
            expense_software_heading
            corporate_cards_expense_software_heading
            yes_value
            no_value
            rows {
                feature
                feature_subtext
                pleo
                corporate_cards
                expense_software
                corporate_cards_expense_software
            }
        }
    }
`
